import { format } from "date-fns";
import { es } from "date-fns/locale";

/**
 * Date Utilities
 * 
 * Funciones reutilizables para formateo de fechas en toda la aplicación
 */

/**
 * Verifica si una fecha es del día actual
 * 
 * @param date - Fecha a verificar
 * @returns true si la fecha es del día actual
 */
export function isToday(date: Date): boolean {
    const now = new Date();
    return (
        date.getDate() === now.getDate() &&
        date.getMonth() === now.getMonth() &&
        date.getFullYear() === now.getFullYear()
    );
}

/**
 * Formatea una fecha mostrando solo la hora si es del día actual,
 * o la fecha completa con hora si es de otro día
 * 
 * @param dateInput - String de fecha o objeto Date
 * @param invalidDateMessage - Mensaje a mostrar si la fecha es inválida (default: "Fecha inválida")
 * @returns Fecha formateada
 * 
 * @example
 * ```ts
 * // Si es hoy (24 mar 2026, 14:30)
 * formatDateSmart("2026-03-24T14:30:00") // "2:30 PM"
 * 
 * // Si es otro día
 * formatDateSmart("2026-03-22T14:30:00") // "22 mar 2026"
 * ```
 */
export function formatDateSmart(
    dateInput: string | Date,
    invalidDateMessage: string = "Fecha inválida"
): string {
    const date = typeof dateInput === "string" ? new Date(dateInput) : dateInput;

    if (isNaN(date.getTime())) {
        return invalidDateMessage;
    }

    // Si es hoy, mostrar solo hora (formato: 2:30 PM)
    if (isToday(date)) {
        return format(date, "p", { locale: es });
    }

    // Si no es hoy, mostrar fecha completa con hora (formato: 22 mar 2026)
    return format(date, "d MMM yyyy", { locale: es });
}

/**
 * Formatea una fecha en formato corto (solo día y mes)
 * 
 * @param dateInput - String de fecha o objeto Date
 * @returns Fecha formateada (ej: "24 mar")
 * 
 * @example
 * ```ts
 * formatDateShort("2026-03-24") // "24 mar"
 * ```
 */
export function formatDateShort(dateInput: string | Date): string {
    const date = typeof dateInput === "string" ? new Date(dateInput) : dateInput;

    if (isNaN(date.getTime())) {
        return "";
    }

    return format(date, "d MMM", { locale: es });
}

/**
 * Formatea una fecha en formato largo (fecha completa sin hora)
 * 
 * @param dateInput - String de fecha o objeto Date
 * @returns Fecha formateada (ej: "24 de marzo de 2026")
 * 
 * @example
 * ```ts
 * formatDateLong("2026-03-24") // "24 de marzo de 2026"
 * ```
 */
export function formatDateLong(dateInput: string | Date): string {
    const date = typeof dateInput === "string" ? new Date(dateInput) : dateInput;

    if (isNaN(date.getTime())) {
        return "";
    }

    return format(date, "PPP", { locale: es });
}

/**
 * Formatea una fecha con fecha y hora completas
 * 
 * @param dateInput - String de fecha o objeto Date
 * @returns Fecha formateada (ej: "24 de marzo de 2026, 2:30 PM")
 * 
 * @example
 * ```ts
 * formatDateTimeFull("2026-03-24T14:30:00") // "24 de marzo de 2026, 2:30 PM"
 * ```
 */
export function formatDateTimeFull(dateInput: string | Date): string {
    const date = typeof dateInput === "string" ? new Date(dateInput) : dateInput;

    if (isNaN(date.getTime())) {
        return "";
    }

    return format(date, "PPpp", { locale: es });
}

/**
 * Formatea solo la hora de una fecha
 * 
 * @param dateInput - String de fecha o objeto Date
 * @returns Hora formateada (ej: "2:30 PM")
 * 
 * @example
 * ```ts
 * formatTimeOnly("2026-03-24T14:30:00") // "2:30 PM"
 * ```
 */
export function formatTimeOnly(dateInput: string | Date): string {
    const date = typeof dateInput === "string" ? new Date(dateInput) : dateInput;

    if (isNaN(date.getTime())) {
        return "";
    }

    return format(date, "p", { locale: es });
}

/**
 * Formatea una fecha usando toLocaleDateString con configuración española
 * 
 * @param dateInput - String de fecha o objeto Date
 * @param options - Opciones de formateo (default: day, month, year)
 * @returns Fecha formateada
 * 
 * @example
 * ```ts
 * formatDateLocale("2026-03-24") // "24 mar 2026"
 * formatDateLocale("2026-03-24", { month: "long" }) // "24 marzo 2026"
 * formatDateLocale("2026-03-24", { dateStyle: "short", timeStyle: "short" }) // "24/3/26, 10:30"
 * ```
 */
export function formatDateLocale(
    dateInput: string | Date,
    options: Intl.DateTimeFormatOptions = {
        day: "2-digit",
        month: "short",
        year: "numeric",
    }
): string {
    const date = typeof dateInput === "string" ? new Date(dateInput) : dateInput;

    if (isNaN(date.getTime())) {
        return "";
    }

    return new Intl.DateTimeFormat("es-ES", options).format(date);
}

/**
 * Calcula el tiempo transcurrido desde una fecha (tiempo relativo)
 * 
 * @param dateInput - String de fecha o objeto Date
 * @returns Texto descriptivo del tiempo transcurrido
 * 
 * @example
 * ```ts
 * getRelativeTime("2026-03-24T14:00:00") // "hace 30 minutos"
 * getRelativeTime("2026-03-23T14:00:00") // "hace 1 día"
 * ```
 */
export function getRelativeTime(dateInput: string | Date): string {
    const date = typeof dateInput === "string" ? new Date(dateInput) : dateInput;

    if (isNaN(date.getTime())) {
        return "";
    }

    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffSeconds = Math.floor(diffMs / 1000);
    const diffMinutes = Math.floor(diffSeconds / 60);
    const diffHours = Math.floor(diffMinutes / 60);
    const diffDays = Math.floor(diffHours / 24);

    if (diffSeconds < 60) {
        return "hace unos segundos";
    } else if (diffMinutes < 60) {
        return `hace ${diffMinutes} minuto${diffMinutes !== 1 ? "s" : ""}`;
    } else if (diffHours < 24) {
        return `hace ${diffHours} hora${diffHours !== 1 ? "s" : ""}`;
    } else if (diffDays < 30) {
        return `hace ${diffDays} día${diffDays !== 1 ? "s" : ""}`;
    } else {
        return formatDateShort(date);
    }
}
